import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  extractAcademicProfileFromApiResponse,
  stripAcademicFieldsFromUserPayload,
} from '../utils/academicProfileFromApi';

/** Backend SSO may send `{ user, academicProfile, codingAccess, projectAccess }`; store a flat user + extras. */
function flattenAuthSessionPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.user && typeof payload.user === 'object' && payload.user.role != null) {
    return {
      ...payload.user,
      academicProfile: payload.academicProfile,
      codingAccess: payload.codingAccess,
      projectAccess: payload.projectAccess,
    };
  }
  return payload;
}

const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      /** Mirrors `user.role` for guards that select `useAuthStore((s) => s.role)`. */
      role: null,
      /** Full shape `{ rollNumber, joinYear, … }` with nulls; kept off `user`. */
      academicProfile: null,
      snackbar: { open: false, message: '', severity: 'info' },

      login: (userData, token) => {
        const flat = flattenAuthSessionPayload(userData);
        const academicProfile = extractAcademicProfileFromApiResponse(
          flat && typeof flat === 'object' ? /** @type {Record<string, unknown>} */ (flat) : null,
          'login'
        );
        const userForStorage = stripAcademicFieldsFromUserPayload(
          flat && typeof flat === 'object' ? /** @type {Record<string, unknown>} */ (flat) : flat
        );
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userForStorage));
        try {
          sessionStorage.setItem('stacktrack-theme-sync-system', '1');
        } catch {
          /* ignore */
        }
        set({
          user: { ...userForStorage, token },
          token,
          role: userForStorage?.role ?? null,
          academicProfile,
          snackbar: { open: false, message: '', severity: 'info' },
        });
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        set({
          user: null,
          token: null,
          role: null,
          academicProfile: null,
          snackbar: { open: true, message: 'You have successfully logged out.', severity: 'info' },
        });
      },

      closeSnackbar: () =>
        set((s) => ({ snackbar: { ...s.snackbar, open: false } })),
    }),
    {
      name: 'auth-storage',
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        role: s.role ?? s.user?.role ?? null,
        academicProfile: s.academicProfile,
      }),
      merge: (persisted, current) => {
        const p = /** @type {Record<string, unknown>} */ (persisted);
        const mergedUser = p?.user;
        const userObj =
          mergedUser && typeof mergedUser === 'object'
            ? /** @type {Record<string, unknown>} */ (mergedUser)
            : null;
        const nestedFromUser =
          userObj?.academicProfile && typeof userObj.academicProfile === 'object'
            ? userObj.academicProfile
            : null;
        const nestedFromPersist =
          p?.academicProfile && typeof p.academicProfile === 'object' ? p.academicProfile : null;
        const flatForAcademic = userObj
          ? {
              ...userObj,
              academicProfile: nestedFromUser || nestedFromPersist || undefined,
            }
          : nestedFromPersist
            ? { academicProfile: nestedFromPersist }
            : null;
        return {
          ...current,
          ...p,
          snackbar: current.snackbar,
          role: p?.role ?? p?.user?.role ?? current.role ?? null,
          academicProfile: extractAcademicProfileFromApiResponse(flatForAcademic, 'persist-rehydrate'),
        };
      },
    }
  )
);

export default useAuthStore;
